import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { Helmet } from '@/components/base/Helmet';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || '');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const category = categories.find((c) => c.slug === activeCategory);
  const categoryArticles = getArticlesByCategory(activeCategory);

  if (!category) {
    return (
      <>
        <Navbar />
        <ScrollToTop />
        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="font-heading text-2xl font-semibold text-foreground-100 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet
        title={`${category.name} - 亚洲自拍完全指南`}
        description={category.description}
        keywords={`亚洲自拍,${category.name},自拍教程,自拍技巧`}
        canonical={`https://ahpinxing.cn/category/${category.slug}`}
      />
      <Navbar />
      <ScrollToTop />
      <main>
        <div className="pt-20 pb-8 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: category.name },
              ]}
            />
            <div className="mt-6 flex flex-col lg:flex-row gap-4 items-start">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500">
                  <i className={`${category.icon} text-2xl md:text-3xl`}></i>
                </div>
              </div>
              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-100 mb-2">
                  {category.name}
                </h1>
                <p className="text-foreground-400 text-sm md:text-base max-w-2xl">{category.description}</p>
                <p className="mt-2 text-xs text-foreground-500">共 {category.articleCount} 篇文章</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background-100 min-h-screen">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <aside className="w-full lg:w-56 flex-shrink-0">
                <div className="lg:sticky lg:top-24">
                  <h3 className="font-heading text-sm font-semibold text-foreground-100 mb-3">所有分类</h3>
                  <nav className="space-y-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap ${
                          activeCategory === cat.slug
                            ? 'bg-primary-50 text-primary-500 font-medium'
                            : 'text-foreground-400 hover:text-foreground-100 hover:bg-background-200'
                        }`}
                      >
                        <i className={`${cat.icon} text-base`}></i>
                        {cat.name}
                      </Link>
                    ))}
                  </nav>
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                {categoryArticles.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                    {categoryArticles.map((article) => (
                      <ArticleCard key={article.id} article={article} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <p className="text-foreground-400">该分类暂无文章</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}